import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Film, TrendingUp, Clock, CheckSquare, Loader2,
  RefreshCw, ArrowRight, Zap, ExternalLink, LinkIcon,
  Megaphone, Instagram, Facebook
} from "lucide-react";

function StatCard({ label, value, icon: Icon, to, variant }) {
  const navigate = useNavigate();
  const isPending = variant === "pending";
  const isGold = variant === "gold";
  const isGreen = variant === "green";

  return (
    <div
      onClick={() => navigate(to)}
      className={`relative bg-studio-raised border rounded-xl p-5 cursor-pointer ${
        isPending ? "border-gold" : isGold ? "border-gold/30" : isGreen ? "border-status-green/30" : "border-[#2a2e36] hover:border-gold/40"
      }`}
      style={{ transition: "border-color 0.2s" }}
      data-testid={`stat-${label.replace(/[^a-z]/gi, "-").toLowerCase()}`}
    >
      {isPending && (
        <div className="absolute inset-0 rounded-xl border-2 border-gold animate-pulse pointer-events-none" />
      )}
      <div className="flex items-center justify-between mb-3">
        <span className="section-label">{label}</span>
        <Icon size={16} className={isPending ? "text-gold" : isGold ? "text-gold" : isGreen ? "text-status-green" : "text-zinc-500"} />
      </div>
      <p className={`stat-number text-2xl font-semibold ${isPending ? "text-gold" : isGreen ? "text-status-green" : "text-zinc-100"}`}>{value}</p>
    </div>
  );
}

function BrandCard({ stat, onSync }) {
  const navigate = useNavigate();
  const { brand, last_posted, next_scheduled, queue_count, content_count } = stat;
  const [syncing, setSyncing] = useState(false);
  const isSetupRequired = brand.setup_required;
  const isAiOnly = brand.content_mode === "ai_only";
  const isPreLaunch = brand.pre_launch;
  const hasVimeo = !!brand.vimeo_token;

  const handleSync = async (e) => {
    e.stopPropagation();
    setSyncing(true);
    try {
      const res = await api.post(`/sync/vimeo/${brand.id}`);
      toast.success(`Synced ${res.data.synced || 0} new videos for ${brand.name}`);
      if (onSync) onSync();
    } catch { toast.error("Sync failed"); }
    setSyncing(false);
  };

  const sourceLabel = isAiOnly
    ? <span className="flex items-center gap-1"><Zap size={10} className="text-gold" />AI generated</span>
    : brand.id === "shutter_and_sound"
      ? "YouTube + Dropbox"
      : brand.id === "rain_on_your_wedding_day"
        ? "Vimeo + Dropbox"
        : hasVimeo ? "Vimeo" : "--";

  const igStatus = brand.ig_handle
    ? brand.ig_handle
    : brand.ig_id ? "connected" : brand.ig_connected === false ? "not connected" : "--";

  const fbStatus = brand.fb_page_id ? "connected" : "not connected";

  return (
    <div
      onClick={() => navigate(`/library?brand=${brand.id}`)}
      className={`bg-studio-raised border rounded-xl p-5 cursor-pointer transition-all duration-200 hover:border-gold/30 hover:shadow-md ${
        isSetupRequired ? "border-dashed border-[#363b45]" : "border-[#2a2e36]"
      }`}
      data-testid={`brand-card-${brand.id}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-3 h-3 rounded-full" style={{ background: brand.color_hex }} />
        <h3 className="font-sans font-semibold text-zinc-100 text-sm">{brand.name}</h3>
        {isPreLaunch && <span className="status-badge status-scheduled">pre-launch</span>}
        {isSetupRequired && <span className="status-badge status-scheduled">setup required</span>}
        <span className="text-xs text-zinc-500 ml-auto font-mono">{brand.short_name}</span>
      </div>

      <div className="space-y-2.5 text-xs">
        <div className="flex justify-between text-zinc-400">
          <span>Content items</span>
          <span className="stat-number text-zinc-200">{content_count}</span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>In queue</span>
          <span className="stat-number" style={{ color: queue_count > 0 ? brand.color_hex : '#c6a96f' }}>
            {queue_count}{queue_count === 0 && <span className="text-[10px] ml-1 opacity-60">needs content</span>}
          </span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>Content source</span>
          <span className="stat-number text-zinc-300">{sourceLabel}</span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>Instagram</span>
          <span className={`stat-number ${igStatus === "connected" || (brand.ig_handle) ? "text-zinc-300" : "text-zinc-600"}`}>
            <Instagram size={10} className="inline mr-1 text-[#c47272]" />{igStatus}
          </span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>Facebook</span>
          <span className={`stat-number ${fbStatus === "connected" ? "text-zinc-300" : "text-zinc-600"}`}>
            <Facebook size={10} className="inline mr-1 text-[#5b8ec4]" />{fbStatus}
          </span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>Last posted</span>
          <span className="stat-number text-zinc-300">{last_posted ? new Date(last_posted).toLocaleDateString() : "--"}</span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>Next scheduled</span>
          <span className="stat-number text-zinc-300">{next_scheduled ? new Date(next_scheduled).toLocaleDateString() : "--"}</span>
        </div>
      </div>

      <div className="flex gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
        {isSetupRequired ? (
          <>
            <Link to="/settings"
              className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg text-xs font-medium transition-colors"
              style={{ background: `${brand.color_hex}18`, color: brand.color_hex, border: `1px solid ${brand.color_hex}30` }}>
              <LinkIcon size={12} /> Connect Accounts
            </Link>
            {brand.website && (
              <a href={brand.website} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold transition-colors">
                <ExternalLink size={12} />
              </a>
            )}
          </>
        ) : (
          <>
            {hasVimeo && (
              <button onClick={handleSync} disabled={syncing}
                className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold hover:border-gold/30 transition-colors disabled:opacity-50">
                {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync
              </button>
            )}
            <Link to="/queue"
              className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold hover:border-gold/30 transition-colors">
              Queue <ArrowRight size={12} />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

function ActivityItem({ event }) {
  const typeIcons = {
    vimeo_sync: RefreshCw, youtube_sync: RefreshCw, post_created: Film,
    post_approved: CheckSquare, post_published: TrendingUp, webhook_received: Film,
  };
  const Icon = typeIcons[event.type] || Film;
  const time = event.created_at ? new Date(event.created_at).toLocaleString() : "";
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#222630] last:border-0">
      <div className="w-7 h-7 rounded-lg bg-studio-surface flex items-center justify-center mt-0.5">
        <Icon size={14} className="text-zinc-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-300 leading-snug">{event.message}</p>
        <p className="text-[11px] text-zinc-600 mt-1 font-mono">{time}</p>
      </div>
    </div>
  );
}

function IntegrationCard({ data }) {
  const { brand, health, cached, color } = data;
  const summary = data.data;
  const metrics = summary?.metrics || [];
  const headline = summary?.headline || "";

  const healthDot = {
    healthy: "bg-status-green",
    stale: "bg-gold",
    offline: "bg-status-red",
    down: "bg-status-red",
    auth_error: "bg-status-red",
    not_configured: "bg-zinc-600",
  };

  const healthLabel = {
    healthy: "live",
    stale: "stale",
    offline: "offline",
    down: "down",
    auth_error: "auth error",
    not_configured: "not configured",
  };

  const formatValue = (metric) => {
    if (metric.unit === "cents") return `$${(metric.value / 100).toFixed(2)}`;
    if (metric.unit === "percent") return `${metric.value}%`;
    if (metric.unit === "seconds") return `${metric.value}s`;
    return metric.value.toLocaleString();
  };

  return (
    <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-5 brand-card" data-testid={`integration-${brand?.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-3 h-3 rounded-full" style={{ background: color || "#71717a" }} />
        <h3 className="text-sm font-semibold text-zinc-100">{brand?.name}</h3>
        <div className="flex items-center gap-1.5 ml-auto">
          <span className={`w-2 h-2 rounded-full ${healthDot[health] || "bg-zinc-600"}`} />
          <span className="text-[10px] font-mono text-zinc-500">{healthLabel[health] || health}</span>
          {cached && <span className="text-[9px] font-mono text-zinc-600 ml-1">(cached)</span>}
        </div>
      </div>

      {headline && <p className="text-xs text-zinc-400 mb-3">{headline}</p>}

      {metrics.length > 0 ? (
        <div className="grid grid-cols-3 gap-3">
          {metrics.slice(0, 6).map((m) => (
            <div key={m.key}>
              <p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider">{m.label}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="stat-number text-sm text-zinc-100">{formatValue(m)}</span>
                {m.change_direction && m.change_pct != null && (
                  <span className={`text-[10px] font-mono ${m.change_direction === "up" ? "text-status-green" : m.change_direction === "down" ? "text-status-red" : "text-zinc-500"}`}>
                    {m.change_direction === "up" ? "+" : ""}{m.change_pct}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-zinc-600 text-center py-3">No data available</p>
      )}
    </div>
  );
}



export default function SocialOverview() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, actRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/activity?limit=10"),
      ]);
      setStats(statsRes.data);
      setActivity(actRes.data.events);
    } catch { toast.error("Failed to load dashboard"); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner-gold" /></div>;
  }

  const pending = stats?.pending_approvals || 0;

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard">
      <div>
        <h1 className="font-display text-3xl font-bold text-zinc-100">Social Management</h1>
        <p className="section-label mt-2">// brand overview</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 stagger-children">
        <StatCard label="// content" value={stats?.total_content || 0} icon={Film} to="/library" />
        <StatCard label="// this week" value={stats?.posts_this_week || 0} icon={TrendingUp} to="/calendar" />
        <StatCard label="// pending" value={pending} icon={Clock} to="/queue"
          variant={pending > 0 ? "pending" : "green"} />
        <StatCard label="// published" value={stats?.published_count || 0} icon={CheckSquare} to="/calendar" />
        <StatCard label="// active ads" value="--" icon={Megaphone} to="/ads" />
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold text-zinc-100 mb-1">Brands</h2>
        <p className="section-label mb-4">// brands</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 stagger-children">
          {stats?.brand_stats?.map((bs) => (
            <BrandCard key={bs.brand.id} stat={bs} onSync={fetchData} />
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold text-zinc-100 mb-1">Recent Activity</h2>
        <p className="section-label mb-4">// latest events</p>
        <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-5">
          {activity.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-6">No recent activity</p>
          ) : (
            activity.map((event) => <ActivityItem key={event.id} event={event} />)
          )}
        </div>
      </div>
    </div>
  );
}
